import React, { useMemo } from "react";
import {
	Show,
	SimpleShowLayout,
	TextField,
	BooleanField,
	DateField,
	FunctionField,
	useTranslate,
} from "react-admin";
import { userPreferences } from "../../utils/fakeUserPrefrences";
import { CustomAsideSimpleList } from "../../customComponents/CustomAsideSimpleList";
import { Typography, Button, Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import GetAppIcon from "@material-ui/icons/GetApp";
import CustomImageField from "../../customComponents/CustomImageField";

const useStyles = makeStyles((theme) => ({
	coverImage: {
		maxWidth: "300px",
		maxHeight: "200px",
		borderRadius: theme.spacing(1),
		objectFit: "cover",
		marginBottom: theme.spacing(1),
	},
	pdfViewer: {
		width: "500px",
		height: "500px",
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	videoPlayer: {
		maxWidth: "400px",
		width: "100%",
		borderRadius: theme.spacing(1),
	},
	downloadButton: {
		marginTop: theme.spacing(1),
		alignSelf: "flex-start",
	},
	mediaSection: {
		marginBottom: theme.spacing(2),
		display: "flex",
		flexDirection: "column",
	},
}));

const CoverImageDisplay = ({ record }) => {
	const classes = useStyles();

	if (!record?.imageCoverUrl) return <Typography>No cover image</Typography>;

	return (
		<Box className={classes.mediaSection}>
			<img
				src={record.imageCoverUrl}
				alt="Handout Cover"
				className={classes.coverImage}
			/>
		</Box>
	);
};

const PDFDisplay = ({ record }) => {
	const classes = useStyles();

	if (!record?.handoutPdfUrl) return <Typography>No PDF attached</Typography>;

	const handleDownload = async () => {
		try {
			const response = await fetch(record.handoutPdfUrl);
			const blob = await response.blob();

			const filename = record.title
				? `${record.title}.pdf`
				: "handout.pdf";

			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();

			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error("Download failed:", error);
			window.open(record.handoutPdfUrl, "_blank");
		}
	};

	return (
		<Box className={classes.mediaSection}>
			<iframe
				src={`https://docs.google.com/gview?url=${encodeURIComponent(
					record.handoutPdfUrl
				)}&embedded=true&widget=false&chrome=false`}
				className={classes.pdfViewer}
				title="PDF Viewer"
			/>
			<Button
				variant="contained"
				color="primary"
				startIcon={<GetAppIcon />}
				onClick={handleDownload}
				size="small"
				className={classes.downloadButton}
			>
				Download PDF
			</Button>
		</Box>
	);
};

const VideoDisplay = ({ record }) => {
	const classes = useStyles();

	if (!record?.handoutVideoUrl)
		return <Typography>No video attached</Typography>;

	return (
		<Box className={classes.mediaSection}>
			<video className={classes.videoPlayer} controls preload="metadata">
				<source src={record.handoutVideoUrl} type="video/mp4" />
				Your browser does not support the video tag.
			</video>
		</Box>
	);
};

const DisplayStatusField = ({ record }) => {
	const sections = ["Pathways"];
	if (record?.showInHandoutsSection) {
		sections.push("Handouts");
	}

	return <Typography>{sections.join(", ")}</Typography>;
};

export const HandoutShow = (props) => {
	const translate = useTranslate();
	const defaultLocale = useMemo(
		() => userPreferences.defaultTranslationLanguage,
		[]
	);

	return (
		<Show {...props} title={translate("handouts.show.title")}>
			<SimpleShowLayout>
				<TextField source="title" label="Handout Title" />
				<TextField source="description" label="Handout Description" />
				<FunctionField
					label="Active Status"
					render={(record) =>
						record?.isActive ? "Active" : "Inactive"
					}
				/>
				<FunctionField
					label="Display Status"
					render={(record) => <DisplayStatusField record={record} />}
				/>
				<FunctionField
					label="Cover Image"
					render={(record) => <CoverImageDisplay record={record} />}
				/>
				<FunctionField
					label="PDF Document"
					render={(record) => <PDFDisplay record={record} />}
				/>
				<FunctionField
					label="Video"
					render={(record) => <VideoDisplay record={record} />}
				/>
				<DateField source="updatedOn" label="Last Updated" showTime />
			</SimpleShowLayout>
		</Show>
	);
};
